import api from "@/lib/api";

/**
 * postWithRetry — safe transient-failure recovery for AI mutation endpoints.
 *
 * Contract:
 *   - One user action = one logical request. Automatic retries carry the
 *     same `Idempotency-Key` header so the backend replays the cached
 *     response and never (a) creates a duplicate DB row or (b) consumes a
 *     second free use.
 *   - Only retries transient failures: network errors, timeouts, 502, 503,
 *     504. Never retries 400 / 401 / 402 / 403 / 422 / 429 — those are
 *     user- or account-actionable and must surface immediately.
 *   - Retries max 3 attempts total with linear back-off: 0ms, 250ms, 750ms.
 *   - Idempotency-Key is generated once per user action and reused for
 *     every retry within that action.
 *
 * Usage:
 *   const data = await postWithRetry("/fitness/plan", payload, { timeout: 60_000 });
 *
 * Returns the parsed response body on success. On failure surfaces the
 * axios error unchanged so callers can `parseAnalyzerError(err)` and
 * render their feature-specific error card.
 */

const NON_RETRYABLE_STATUSES = new Set([400, 401, 402, 403, 404, 409, 410, 422, 429]);
const MAX_ATTEMPTS = 3;
const BACKOFF_MS = [0, 250, 750]; // relative to attempt index

export function newIdempotencyKey(scope = "act") {
  try {
    if (window.crypto?.randomUUID) {
      return `${scope}-${window.crypto.randomUUID()}`;
    }
  } catch { /* ignore */ }
  return `${scope}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export async function postWithRetry(url, payload, options = {}) {
  const {
    timeout = 60_000,
    headers = {},
    idempotencyKey,
    scope = "act",
    signal,
  } = options;
  const key = idempotencyKey || newIdempotencyKey(scope);
  const composedHeaders = { ...headers, "Idempotency-Key": key };

  let lastErr = null;
  for (let i = 0; i < MAX_ATTEMPTS; i++) {
    try {
      const { data } = await api.post(url, payload, {
        timeout,
        headers: composedHeaders,
        signal,
      });
      return data;
    } catch (err) {
      lastErr = err;
      const status = err?.response?.status;
      // User-actionable — surface immediately.
      if (status && NON_RETRYABLE_STATUSES.has(status)) throw err;
      // Explicit user cancellation.
      if (err?.name === "CanceledError" || err?.name === "AbortError") throw err;
      // Any other failure (network, 5xx, timeout) → back off and retry.
      if (i < MAX_ATTEMPTS - 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, BACKOFF_MS[i + 1] ?? 750));
      }
    }
  }
  throw lastErr;
}
